import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemberAuthStore } from "modules/members/auth";
import { membersApi, useMemberGetQuery } from "modules/members/api";
import { SR } from "modules/api/websockets";
import { mimeForFile } from "./lunaAttachments";

// Types
export interface LunaSession {
  id: string;
  status: "idle" | "thinking" | "waiting_for_tool" | "complete" | "error" | "pending_external" | "closed";
  createdAt: string;
  updatedAt: string;
  title?: string;
  pending_tool_count?: number;
}

export interface LunaPlaybook {
  id: string;
  name: string;
  description: string;
  summary?: string;
  icon?: string;
}

// Block type used in luna_blocks artifacts
export interface ArtifactBlock {
  id?: string;
  type: string;
  content?: string | Record<string, unknown>;
  title?: string;
  description?: string;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

// Base artifact fields shared by all artifact types
interface BaseArtifact {
  id: string;
  sessionId: string;
  title: string;
  label: string | null;
  description: string | null;
  versionNumber: number;
  createdAt: string;
  updatedAt: string;
}

// Luna blocks artifact - renders using BlockRenderer
// Content is a nested array (same structure as message content): [[block], [block], ...]
export interface LunaBlocksArtifact extends BaseArtifact {
  type: "luna_blocks";
  content: ArtifactBlock[][];
}

// Subagent artifact - content is an array with a session_id object (content is always an array from the API)
export interface SubagentArtifact extends BaseArtifact {
  type: "subagent";
  content: Array<{ session_id: string }>;
}

// Discriminated union of all artifact types
export type Artifact = LunaBlocksArtifact | SubagentArtifact;

// API Functions (for use in services, not components)
export const useLunaApi = () => {
  const accessToken = useMemberAuthStore((state) => state.accessToken);

  const createChatSession = async (): Promise<{ sessionId: string }> => {
    const response = await membersApi.post<{ sessionId: string }>(
      "members/agents/start-session",
      { type: "chat" },
      accessToken as string,
    );

    if (response.success) {
      return { sessionId: response.data.sessionId };
    }
    throw new Error("Failed to create session");
  };

  const createPlaybookSession = async (
    playbookId: string,
    context?: string,
  ): Promise<{ sessionId: string }> => {
    const body: { playbook_id: string; context?: string } = {
      playbook_id: playbookId,
    };
    if (context) {
      body.context = context;
    }

    const response = await membersApi.post<{ sessionId: string }>(
      "members/agents/playbook-session",
      body,
      accessToken as string,
    );

    if (response.success) {
      return { sessionId: response.data.sessionId };
    }
    throw new Error("Failed to create playbook session");
  };

  const rateMessage = async (
    sessionId: string,
    messageId: string,
    rating: "good" | "bad",
  ): Promise<void> => {
    await membersApi.post(
      `members/agents/${sessionId}/messages/${messageId}/rating`,
      { rating },
      accessToken as string,
    );
  };

  // Step 1 of the attachment flow: request a presigned PUT URL. The declared
  // content_type is derived from the file extension (see mimeForFile) so it
  // matches what we send on the PUT and in the message payload. The returned
  // URL has a ~120s TTL, so the caller must PUT immediately (see
  // uploadFileToS3) rather than holding the URL.
  const getAttachmentUploadUrl = async (
    sessionId: string,
    file: File,
  ): Promise<{ s3Key: string; uploadUrl: string; expiresAt: string }> => {
    const response = await membersApi.post<{
      s3Key: string;
      uploadUrl: string;
      expiresAt: string;
    }>(
      "members/luna/attachment-upload-url",
      {
        session_id: sessionId,
        filename: file.name,
        content_type: mimeForFile(file),
        size: file.size,
      },
      accessToken as string,
    );

    if (response.success) {
      return response.data;
    }
    throw new Error("Failed to request upload URL");
  };

  // Step 2: PUT the raw bytes straight to S3. The Content-Type must match the
  // content_type declared in step 1 or S3 rejects with a signature error;
  // Content-Length is set automatically by the browser from the file body.
  const uploadFileToS3 = (
    uploadUrl: string,
    file: File,
    contentType: string,
    onProgress?: (fraction: number) => void,
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader("Content-Type", contentType);
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            onProgress(e.loaded / e.total);
          }
        };
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error("Upload failed"));
        }
      };
      xhr.onerror = () => reject(new Error("Upload failed"));
      xhr.send(file);
    });
  };

  return {
    createChatSession,
    createPlaybookSession,
    rateMessage,
    getAttachmentUploadUrl,
    uploadFileToS3,
  };
};

// React Query Hooks (for components via state files)
export const useGetLunaSessions = (limit = 20) => {
  const accessToken = useMemberAuthStore((state) => state.accessToken);

  return useInfiniteQuery(
    ["luna-sessions", accessToken],
    async ({ pageParam = 0 }) => {
      const response = await membersApi.get<LunaSession[]>(
        `members/agents/sessions?pagination[limit]=${limit}&pagination[after]=${pageParam}&pagination[sort_direction]=desc&pagination[sort_by]=updated_at`,
        accessToken as string,
      );

      if (response.success) {
        const sessions = response.data;
        return {
          rows: sessions,
          after: sessions.length === limit ? pageParam + limit : undefined,
          hasMore: sessions.length === limit,
        };
      }
      throw new Error("Failed to fetch sessions");
    },
    {
      getNextPageParam: (lastPage) => lastPage.after,
      refetchOnWindowFocus: false,
      suspense: false,
      enabled: Boolean(accessToken),
    },
  );
};

export const useGetLunaPlaybooks = () => {
  return useMemberGetQuery<LunaPlaybook[]>(
    ["luna-playbooks"],
    "/members/agents/playbooks",
    { suspense: false },
  );
};

export const useRateLunaMessage = (sessionId: string) => {
  const accessToken = useMemberAuthStore((state) => state.accessToken);

  const rateMessage = async (body: { messageId: string; rating: "good" | "bad" }) => {
    return membersApi.post(
      `members/agents/${sessionId}/messages/${body.messageId}/rating`,
      { rating: body.rating },
      accessToken as string,
    );
  };

  return { mutate: rateMessage };
};

export interface LunaRequest {
  sessionId: string;
  title: string | null;
  status: "waiting_response" | "thinking" | "closed";
  updatedAt: string;
}

export const useGetLunaRequests = () => {
  return useMemberGetQuery<LunaRequest[]>(
    ["luna-requests"],
    "/members/luna/requests",
    { suspense: false },
  );
};

export const useGetArtifact = (artifactId: string | null) => {
  return useMemberGetQuery<Artifact>(
    ["luna-artifact", artifactId],
    artifactId ? `/members/agents/artifacts/${artifactId}` : "",
    {
      enabled: !!artifactId,
      suspense: false,
    },
  );
};
